import { useCallback, useRef } from 'react';

function easeOutCirc(t: number): number {
    return Math.sqrt(1 - Math.pow(t - 1, 2));
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AnimatedScrollOptions {
    msPerItem?: number;
    minDuration?: number;
    itemHeight?: number;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAnimatedScroll(options: AnimatedScrollOptions = {}) {
    const { msPerItem = 150, minDuration = 400, itemHeight: fixedItemHeight } = options;

    // Track active RAF so we can cancel if a new scroll starts mid-animation
    const rafRef = useRef<number | null>(null);

    const cancelActive = useCallback(() => {
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    }, []);

    // ── Core animator ─────────────────────────────────────────────────────────

    const animateTo = useCallback(
        (element: HTMLElement, targetScrollTop: number, duration: number) => {
            cancelActive();
            const start = element.scrollTop;
            const distance = targetScrollTop - start;
            if (Math.abs(distance) < 1) return;

            const startTime = performance.now();

            function step(now: number) {
                const progress = Math.min((now - startTime) / duration, 1);
                element.scrollTop = start + distance * easeOutCirc(progress);
                if (progress < 1) {
                    rafRef.current = requestAnimationFrame(step);
                } else {
                    rafRef.current = null;
                }
            }

            rafRef.current = requestAnimationFrame(step);
        },
        [cancelActive]
    );

    // ── Clamp target to actual scrollable max ─────────────────────────────────

    const clampTarget = useCallback((container: HTMLElement, target: number): number => {
        const max = Math.max(0, container.scrollHeight - container.clientHeight);
        return Math.min(target, max);
    }, []);

    // ── Measure item height from DOM ──────────────────────────────────────────

    const measureItemHeight = useCallback(
        (container: HTMLElement): number => {
            if (fixedItemHeight) return fixedItemHeight;
            const el = container.querySelector('[data-scroll-item]') as HTMLElement | null;
            return el?.offsetHeight ?? 30;
        },
        [fixedItemHeight]
    );

    // ── Public methods ────────────────────────────────────────────────────────

    const scrollTo = useCallback(
        (element: HTMLElement | null, targetScrollTop: number) => {
            if (!element) return;
            const target = clampTarget(element, targetScrollTop);
            const distance = Math.abs(target - element.scrollTop);
            if (distance < 1) return;
            const itemH = measureItemHeight(element);
            const duration = Math.max((distance / itemH) * msPerItem, minDuration);
            animateTo(element, target, duration);
        },
        [animateTo, clampTarget, measureItemHeight, msPerItem, minDuration]
    );

    // Scroll to center a specific child element in its container.
    const scrollToCenter = useCallback(
        (container: HTMLElement | null, item: HTMLElement | null) => {
            if (!container || !item) return;
            const raw = item.offsetTop - container.clientHeight / 2 + item.clientHeight / 2;
            scrollTo(container, Math.max(0, raw));
        },
        [scrollTo]
    );

    const scrollToSelector = useCallback(
        (container: HTMLElement | null, selector: string) => {
            if (!container) return;
            const item = container.querySelector(selector) as HTMLElement | null;
            scrollToCenter(container, item);
        },
        [scrollToCenter]
    );

    const scrollToIndex = useCallback(
        (container: HTMLElement | null, index: number, itemH: number) => {
            if (!container) return;
            const raw = index * itemH - container.clientHeight / 2 + itemH / 2;
            scrollTo(container, Math.max(0, raw));
        },
        [scrollTo]
    );

    const scrollToIndexFast = useCallback(
        (
            container: HTMLElement | null,
            index: number,
            itemH: number,
            opts: { preJumpItems?: number; animationDuration?: number } = {}
        ) => {
            if (!container) return;
            cancelActive();

            const { preJumpItems = 8, animationDuration } = opts;

            const raw = index * itemH - container.clientHeight / 2 + itemH / 2;
            const targetScrollTop = clampTarget(container, Math.max(0, raw));

            container.scrollTop = Math.max(0, targetScrollTop - preJumpItems * itemH);

            const duration = animationDuration ?? Math.max(preJumpItems * msPerItem, minDuration);
            rafRef.current = requestAnimationFrame(() => {
                animateTo(container, targetScrollTop, duration);
            });
        },
        [animateTo, cancelActive, clampTarget, msPerItem, minDuration]
    );

    const scrollToSelectorFast = useCallback(
        (
            container: HTMLElement | null,
            selector: string,
            opts: { preJumpItems?: number; animationDuration?: number } = {}
        ) => {
            if (!container) return;
            cancelActive();

            const { preJumpItems = 8, animationDuration } = opts;

            const targetEl = container.querySelector(selector) as HTMLElement | null;
            if (!targetEl) return;

            const itemH = targetEl.offsetHeight || measureItemHeight(container);
            const raw = targetEl.offsetTop - container.clientHeight / 2 + targetEl.offsetHeight / 2;
            const targetScrollTop = clampTarget(container, Math.max(0, raw));

            container.scrollTop = Math.max(0, targetScrollTop - preJumpItems * itemH);

            const duration = animationDuration ?? Math.max(preJumpItems * msPerItem, minDuration);
            rafRef.current = requestAnimationFrame(() => {
                animateTo(container, targetScrollTop, duration);
            });
        },
        [animateTo, cancelActive, clampTarget, measureItemHeight, msPerItem, minDuration]
    );

    return {
        scrollTo,
        scrollToCenter,
        scrollToSelector,
        scrollToIndex,
        scrollToIndexFast,
        scrollToSelectorFast
    };
}
